import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Autocomplete, CircularProgress,
    Box, Typography, ToggleButton, ToggleButtonGroup, Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { getCombosJWT, pasesEnviarJWT, pasesPrioridadesJWT } from '../../api/endpoints';
import { capitalizar } from '../../utils/capitalizar';

const prioridadColors = {
    0: { bg: '#f5f5f5', border: '#bbb', text: '#666' },
    1: { bg: '#f5f5f5', border: '#bbb', text: '#666' },
    2: { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0' },
    3: { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
    4: { bg: '#ffebee', border: '#f44336', text: '#c62828' },
};

function EnviarPaseDialog({ open, onClose, idExpediente, onEnviado }) {
    const [destinoUsuario, setDestinoUsuario] = useState(null);
    const [usuariosOptions, setUsuariosOptions] = useState([]);
    const [loadingUsuarios, setLoadingUsuarios] = useState(false);
    const [inputUsuario, setInputUsuario] = useState('');
    const [prioridad, setPrioridad] = useState(2); // 2 = Normal
    const [prioridades, setPrioridades] = useState([]);
    const [observaciones, setObservaciones] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setError('');
            pasesPrioridadesJWT().then(res => {
                if (res?.ok) setPrioridades(res.prioridades || []);
            }).catch(() => {});
        }
    }, [open]);

    useEffect(() => {
        if (!inputUsuario || inputUsuario.length < 2) {
            setUsuariosOptions([]);
            return;
        }
        setLoadingUsuarios(true);
        const timeout = setTimeout(() => {
            getCombosJWT('personal', inputUsuario, 15).then(res => {
                if (res?.ok) {
                    // Solo mostrar personas que tengan usuario asignado en el sistema
                    const conUsuario = (res.items || []).filter(item => item.usuario);
                    setUsuariosOptions(conUsuario);
                }
            }).catch(() => {}).finally(() => setLoadingUsuarios(false));
        }, 300);
        return () => clearTimeout(timeout);
    }, [inputUsuario]);

    const handleEnviar = async () => {
        if (!destinoUsuario) return;
        if (!destinoUsuario.usuario) {
            setError('Esta persona no tiene usuario en el sistema');
            return;
        }
        setEnviando(true);
        setError('');
        try {
            const res = await pasesEnviarJWT(idExpediente, {
                destinoUsuario: destinoUsuario.usuario,
                prioridad,
                observaciones,
            });
            if (res?.ok) {
                onEnviado?.();
                handleClose();
            } else {
                setError(res?.mensaje || 'Error al enviar el pase');
            }
        } catch (err) {
            console.error('Error al enviar pase:', err);
            setError('Error de conexión al enviar el pase');
        } finally {
            setEnviando(false);
        }
    };

    const handleClose = () => {
        setDestinoUsuario(null);
        setInputUsuario('');
        setPrioridad(2);
        setObservaciones('');
        setError('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
            <DialogTitle sx={{ pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SendIcon color="primary" sx={{ fontSize: 22 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Enviar expediente</Typography>
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2.5} pt={1.5}>
                    {error && (
                        <Alert severity="error" onClose={() => setError('')} sx={{ py: 0 }}>
                            {error}
                        </Alert>
                    )}

                    <Autocomplete
                        options={usuariosOptions}
                        getOptionLabel={(opt) => capitalizar(opt.label) || ''}
                        filterOptions={(x) => x}
                        value={destinoUsuario}
                        onChange={(_, val) => { setDestinoUsuario(val); setError(''); }}
                        inputValue={inputUsuario}
                        onInputChange={(_, val) => setInputUsuario(val)}
                        loading={loadingUsuarios}
                        noOptionsText={inputUsuario.length < 2 ? 'Escribí al menos 2 letras...' : 'Sin resultados'}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Destinatario"
                                placeholder="Buscar por nombre o apellido..."
                                required
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <PersonSearchIcon sx={{ color: 'action.active', mr: 0.5, fontSize: 20 }} />
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        endAdornment: (
                                            <>
                                                {loadingUsuarios ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />

                    {prioridades.length > 0 && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                                Prioridad
                            </Typography>
                            <ToggleButtonGroup
                                value={prioridad}
                                exclusive
                                onChange={(_, val) => { if (val !== null) setPrioridad(val); }}
                                size="small"
                                fullWidth
                                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.8rem', py: 0.5 } }}
                            >
                                {prioridades.map(p => {
                                    const c = prioridadColors[p.id] || prioridadColors[1];
                                    return (
                                        <ToggleButton
                                            key={p.id}
                                            value={p.id}
                                            sx={{
                                                '&.Mui-selected': {
                                                    backgroundColor: c.bg,
                                                    borderColor: c.border,
                                                    color: c.text,
                                                    fontWeight: 600,
                                                    '&:hover': { backgroundColor: c.bg },
                                                },
                                            }}
                                        >
                                            {p.nombre}
                                        </ToggleButton>
                                    );
                                })}
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    <TextField
                        label="Observaciones"
                        multiline
                        rows={2}
                        size="small"
                        value={observaciones}
                        onChange={e => setObservaciones(e.target.value)}
                        inputProps={{ maxLength: 100 }}
                        helperText={`${observaciones.length}/100`}
                        placeholder="Opcional..."
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={handleClose} disabled={enviando} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleEnviar}
                    disabled={!destinoUsuario || enviando}
                    startIcon={enviando ? <CircularProgress size={16} /> : <SendIcon />}
                    sx={{ textTransform: 'none', borderRadius: 1, px: 3 }}
                >
                    Enviar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EnviarPaseDialog;
